// ⚠️ FICHIER ENGENDRÉ — NE PAS ÉDITER À LA MAIN.
// Source : lib/operateur-leviers.js + lib/leviers/*.js
// Régénérer : node scripts/generer-miroir-leviers.js
// Pourquoi ce doublon : CRA/webpack refuse les imports hors de src/ (Jest, lui,
// les accepte — d'où le risque de tests verts sur un build cassé).
// lib/leviers/voyageurs.js — LA FAMILLE « VOYAGEURS ».
//
// Neuf actions au périmètre (inventaire du 2026-09-09, § reservation /
// signalement / caution / journey / sejours). **Deux entrent.** Le tri n'a rien
// d'une frilosité — Dimitri, le même jour : « on n'écarte pas par prudence » —
// mais cette famille est la seule dont les colonnes sont écrites par un AUTRE
// écrivain que l'écran, et c'est E3 qui tranche, pas le confort.
//
// ══ CE QUI EST ÉCARTÉ, ET POURQUOI, UN PAR UN ══════════════════════════════
//
// ── `reservation_set_caution_status` et `reservation_set_payment_status` : E3.
// Lus dans `lib/reservation-handlers.js` (et non sur leur nom, qui promet un
// « statut ») : ces deux toggles n'écrivent AUCUN statut. Ils posent UNE
// colonne de date — `caution_received_at`, `payment_received_at` — et rien
// d'autre. Or `api/stripe-webhook.js` écrit ces mêmes colonnes GROUPÉES avec
// tout le reste :
//   · `applyCautionStatus` → `caution_status` + `caution_received_at` +
//     `caution_payment_intent_id` + `caution_checkout_session_id`, puis
//     `propagateDBR` vers `direct_booking_requests` ;
//   · `applyPaiementSejourStatus` → `paiement_sejour_status` + `paid_at` +
//     `payment_received_at` + l'intent, même propagation.
// Le toggle admin est donc l'écrivain PARTIEL de colonnes qu'un autre complète
// — la définition littérale d'E3, née de #1327 où un cron recopiait
// `caution_status` sans la propagation du webhook. Le remède prévu par E3
// (« appeler l'action officielle qui fait tout le travail ») n'existe pas ici :
// l'écrivain complet est le WEBHOOK STRIPE, qui n'est pas une action routée et
// ne peut donc pas être proposée. Rien à substituer ⇒ rien à déclarer.
//
// ⚠️ ET IL Y A PIRE QUE L'INCOMPLÉTUDE, VÉRIFIÉ DANS LE MOTEUR : poser
// `caution_received_at` DÉCLENCHE UN ENVOI. `lib/messages-engine.js` (trigger
// `event_caution_ok`, fenêtre 5 min) ramasse les résas dont ce timestamp vient
// d'être posé — et le commentaire y nomme explicitement « le toggle admin »
// comme deuxième chemin. Un geste dont le titre annonce « marquer la caution
// comme reçue » enverrait un message au voyageur sans que la carte le dise :
// l'effet DÉBORDE le libellé, exactement ce que Dimitri refuse d'approuver
// d'un clic.
//
// ── `booking_cancel_paid` : INTERDIT ABSOLU (`lib/operateur-leviers.js`).
// Remboursement + annulation de caution + réouverture Beds24 en une
// orchestration. Trois irréversibles d'un coup. Seul interdit absolu de mon
// périmètre — les huit autres ont été recoupés contre la liste, aucun autre
// n'y figure.
//
// ── `signalement_voyageur_supprimer` : E1, sans nuance. Lu dans
// `lib/signalement-handlers.js` : `.delete()` SEC sur la table, puis
// `storage.remove(paths)` sur les photos — best-effort assumé, « on ne
// ressuscite rien sur un échec Storage ». Il ne reste qu'une trace technique
// (id + auteur) dans la table de trace. E1 nomme DELETE dur et Storage : les
// deux sont là.
//
// ── `signalement_voyageur_traiter` : DÉJÀ déclaré (famille `operateur`, et il
// y est autonome). L'agrégation lève sur un doublon — le redéclarer ici ferait
// refuser le serveur au démarrage.
//
// ── `reservation_update_internal` et `journey_composition_set` : ÉCARTÉS AU
// PREMIER PASSAGE pour une raison de SOCLE, pas de risque — leur charge utile
// est un OBJET, et `lib/operateur-geste.js` refusait alors tout argument non
// scalaire. LES DEUX SONT REVENUS DEPUIS, et c'est la bonne fin de l'histoire :
// le socle accepte les objets BORNÉS (`args_formes`, clés fermées, valeurs
// fermées ou texte plafonné), `journey_composition_set` a été déclaré le
// 2026-09-09, `reservation_update_internal` le 2026-09-12 (entrée ci-dessous).
// ⚠️ On garde la trace du détour : un levier écarté pour une limite de SOCLE
// n'est pas un levier refusé, c'est un levier en dette — et une dette qu'on
// n'écrit pas devient un refus par prescription.
//
// ── `contracts_orphans_delete` : E1, et pas de ceux qui se discutent. Lu dans
// `lib/contracts-orphans-handlers.js` : `storage.remove(paths)` sur tous les
// fichiers du dossier `contracts/<request_id>/` — des PDF de CONTRATS SIGNÉS.
// La garde serveur est bonne (elle re-vérifie que la demande de réservation
// n'existe plus, 409 `not_orphan` sinon), mais elle protège contre la mauvaise
// cible, pas contre la perte : un PDF effacé du Storage ne se régénère pas,
// il faudrait le refaire signer. Et E2 tombe aussi — ce que Dimitri « verrait »
// après le clic, c'est un compteur (`deleted: N`) et un dossier vide, jamais ce
// qu'il vient de perdre. S'ajoute le fait que l'argument est le `request_id`
// d'une demande DÉJÀ disparue : l'opérateur ne peut le tenir que de l'écran de
// ménage, où Dimitri l'a déjà sous les yeux — la carte n'apporterait rien et
// ferait porter à un clic ce qu'un clic ne peut pas défaire.

const LEVIERS_VOYAGEURS = Object.freeze({
  // ── Le rangement de fin de séjour ────────────────────────────────────────
  //
  // Vague 2 (« rangement et clôtures ») et pas 1 : le geste n'a pas d'inverse
  // déclaré — aucun `sejours_demarquer` n'existe. Le retrait, lui, existe, mais
  // il est AUTOMATIQUE et hors de nos mains : le trigger
  // `zzz_sejour_termine_coherence` efface le marqueur si `end_date` redevient
  // future. Un marquage de travers ne se défait donc pas d'un clic.
  //
  // ⚠️ `taille` N'EST PAS DANS LA LISTE, ET C'EST LE POINT DE CE LEVIER.
  // Le handler l'accepte et ne le borne PAS (`choisirLot` prend n'importe quel
  // entier positif ; le SELECT amont remonte 500 lignes). Dimitri a demandé
  // « par lot de dix, en commençant par les plus vieux » précisément pour VOIR
  // ce qui se marque. Laisser l'opérateur choisir la taille lui rendrait le
  // rattrapage en masse qu'on a écarté — la liste fermée est ce qui grave le
  // dix. Reste `confirmer` : sans lui, le geste ne fait que MONTRER le lot.
  sejours_marquer_lot: {
    ecran: "operateur",
    endpoint: "/api/beds24-mapping",
    action: "sejours_marquer_lot",
    args_autorises: ["confirmer"],
    titre: "Marquer les dix plus anciens séjours terminés",
    // Honnêteté sur E2 : `sejours_termines` n'a AUCUN écran dédié (table fermée
    // par RLS, lue par personne d'autre). Le constat se relit par l'aperçu du
    // même geste — c'est immédiat, chiffré, et ça suffit à dire NON si rien ne
    // s'est écrit (`marques` vaut ce qui est REVENU de l'INSERT, pas ce qu'on
    // comptait écrire).
    constatable:
      "l'aperçu du même geste, relancé, annonce dix séjours marqués de plus et dix restants de moins — avec la liste des écartés (annulés, sans date de fin)",
    sujet: "voyageur",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    // Aucun `sejours_demarquer` n'existe : le retrait est AUTOMATIQUE et hors
    // de nos mains (trigger `zzz_sejour_termine_coherence`, si `end_date`
    // redevient future).
    sans_retour:
      "aucun geste ne démarque : le marqueur ne s'efface que tout seul, si la date de fin du séjour redevient future",
    ne_fait_pas:
      "il ne facture rien, n'envoie rien et ne clôture aucun ménage : il pose un marqueur de rangement sur les dix plus anciens séjours terminés, et rien d'autre. Le lot fait toujours DIX — la taille ne se choisit pas.",
    apercu_absent:
      "le geste EST son propre aperçu : sans `confirmer`, il MONTRE le lot (les dix visés, les écartés et pourquoi) sans rien écrire. C'est le seul levier du registre dans ce cas.",
    dit_comme: [
      "Range les vieux séjours terminés",
      "Fais le ménage dans les séjours finis",
      "Classe les dix plus anciens séjours",
    ],
    vague: 2,
    motif_requis: false,
    autonome: false,
  },

  // ── Le lien de dépôt de caution Booking ──────────────────────────────────
  //
  // ⚠️ VÉRIFIÉ SUR LE CODE, parce que le nom fait peur : ce handler
  // (`lib/livret-sections-handlers.js::handleCautionCheckoutStartOta`)
  // N'APPELLE JAMAIS STRIPE. Il crée — ou retrouve — une ligne `form_tokens`
  // de type `payment_caution` et rend l'URL `/paiement/<token>`. Aucune
  // session, aucune empreinte, aucun hold : c'est `api/stripe.js` qui crée la
  // session quand LE VOYAGEUR ouvre la page, et le débit n'existe qu'à son
  // clic « payer ». Le garde-fou du dépôt (« checkout_start crée le bloc/hold
  // sans mouvement d'argent ») est donc encore plus vrai ici — il ne crée même
  // pas de hold.
  //
  // Marqué `argent` quand même, et volontairement : la seule écriture hors
  // token est le VERROU DU MONTANT (`caution_amount_eur` posé depuis la DB ou
  // la config si la colonne était vide — jamais depuis le client), et surtout
  // le geste met une page de paiement réelle devant un voyageur. La carte doit
  // porter le mot « caution » avant le clic, pas après.
  //
  // Vague 4 et non 1 : rien ne se défait ici d'un geste (le token vit jusqu'à
  // son expiration), et c'est le seul levier de la famille qui aboutit chez un
  // tiers. Il s'ouvre en dernier des vagues bornées.
  //
  // Pas de marquage `sortant` : le handler n'ENVOIE rien. Il rend une URL ;
  // c'est un autre geste (le renvoi de lien) qui la ferait partir. Marquer
  // « sortant » ici dirait à Dimitri qu'un message part — ce serait faux, et un
  // marquage faux use la confiance dans les vrais.
  //
  // ⚠️ L'ARGUMENT EST LE TOKEN LIVRET, PAS LA RÉSERVATION, et c'est structurel :
  // l'anti-IDOR du handler dérive la résa du token seul, `reservation_id` n'est
  // JAMAIS lu du corps. L'opérateur ne pourra donc composer ce geste que si le
  // token livret lui est donné — la garde reste entière même s'il se trompe de
  // séjour, puisqu'un token désigne une résa et une seule. Les HUIT refus
  // (source non Booking, caution éteinte pour ce logement, résa non active,
  // caution déjà satisfaite, hors fenêtre J-2, fenêtre close après le départ,
  // formulaire d'accueil manquant, montant de caution absent) sont côté serveur :
  // une intention mal visée échoue franchement.
  //
  // ⚠️ LE COMPTE A BOUGÉ DEUX FOIS, ET LA SECONDE EST UNE DETTE REFERMÉE.
  // « SIX » était faux (le montant de caution absent manquait) → SEPT. Puis
  // SEPT était faux à son tour : la matrice de caution du logement n'était
  // vérifiée NULLE PART sur ce chemin (dette tracée ici, fermée par #1518). Elle
  // l'est désormais, au même endroit et avec le MÊME prédicat que l'affichage du
  // bouton et que le chemin Stripe : `matrixCautionOn(logement × source)`
  // (`lib/journey-composition-resolver.js:239`), posé juste après le refus de
  // source — le premier point où logement ET plateforme sont connus. Sur un
  // logement où la caution Booking a été éteinte au produit, le lien ne se
  // fabrique plus : 400 `caution_not_due`, zéro écriture.
  //
  // ⚠️ ET LE REFUS EST FAIL-OPEN : matrice ILLISIBLE (≠ vide) → règle par défaut
  // → la caution est demandée, ET Dimitri reçoit une alerte nommant le logement
  // (dédoublonnée 24 h). Un voyageur légitime n'est jamais bloqué par un
  // incident base ; un fail-open muet, lui, ferait croire à une caution éteinte.
  caution_checkout_start_ota: {
    ecran: "calendrier",
    endpoint: "/api/beds24-mapping",
    action: "caution_checkout_start_ota",
    args_autorises: ["token"],
    titre: "Préparer le lien de dépôt de caution",
    constatable:
      "le lien de caution apparaît dans l'onglet Paiements de la réservation, avec sa date d'expiration — aucun euro ne bouge tant que le voyageur n'a pas payé",
    sujet: "voyageur",
    cout: ["argent"],
    // ⚠️ RIEN NE PART : le handler n'appelle pas Stripe et n'envoie aucun
    // message. Il rend une URL. C'est un autre geste qui la ferait partir —
    // d'où l'absence de marquage `sortant`, déjà argumentée plus haut.
    ou_ca_part: ["personne"],
    sans_retour:
      "le lien vit jusqu'à son expiration et ne se révoque pas d'un geste : une fois créé, il reste ouvrable par qui l'a",
    // ⚠️ LA CONFUSION EST DANS LE MOT « CAUTION » : personne n'est débité, et
    // personne n'est prévenu non plus.
    ne_fait_pas:
      "aucun euro ne bouge et aucune empreinte n'est prise : ni session Stripe, ni hold. Et le lien n'est ENVOYÉ à personne — il faut le transmettre au voyageur par un autre chemin.",
    apercu_absent:
      "aucune lecture ne rend l'état de la caution de ce séjour (montant attendu, déjà satisfaite ?, dans la fenêtre J-2 ?) : il faudrait `caution_etat_lire`. Les huit refus du serveur arrivent aujourd'hui APRÈS le clic — dont la matrice caution du logement, désormais vérifiée (la dette est refermée).",
    dit_comme: [
      "Prépare le lien de caution pour ce voyageur Booking",
      "Il me faut une page de caution pour ce séjour",
      "Fais-moi le lien de dépôt de garantie",
    ],
    vague: 4,
    motif_requis: false,
    marquages: ["argent"],
    autonome: false,
  },

  // ── LES QUATRE CHAMPS INTERNES D'UNE RÉSA OTA ────────────────────────────
  //
  // ⚠️ LE SECOND LEVIER QUE MON SOCLE AVAIT COÛTÉ (cf. l'en-tête, et la note de
  // `_forme.js` qui le nomme). Écarté au premier passage faute d'objet borné,
  // déclaré ici une fois `args_formes` en place — pas parce que la prudence a
  // reculé, parce que l'obstacle n'existait plus.
  //
  // ⚠️ ET IL PASSE E3, CE QUI N'EST PAS ÉVIDENT DANS CETTE FAMILLE : la
  // whitelist serveur `RESERVATION_SCOPE_B_WHITELIST` (`lib/reservation-handlers.js`)
  // ne compte QUE 4 clés, et son commentaire dit pourquoi les autres sont
  // dehors — `guest_email`, `guest_phone`, `notes`, `arrival_time`,
  // `guest_country` sont exclus parce que le cron de synchro Beds24 les
  // écraserait. Autrement dit : le serveur a déjà fait le tri d'E3 à notre
  // place, et ces quatre champs-là n'ont pas d'autre écrivain. ZÉRO argent dans
  // la liste, aucun montant, aucun statut de paiement.
  //
  // ⚠️ TOUT ARRIVE EN TEXTE, Y COMPRIS LE NOMBRE D'ENFANTS, et c'est voulu :
  // `texte: true` borne les valeurs à des chaînes, et le serveur fait
  // `Number(val)` puis `Number.isInteger` — « 2 » passe, « deux » est refusé
  // franchement. Les trois champs d'adresse sont plafonnés à 200 caractères
  // côté serveur (le plafond du socle, 4 000, est plus large : c'est le serveur
  // qui tranche, jamais nous). Une chaîne VIDE efface le champ.
  //
  // Vague 9 : une adresse postale, c'est un texte que Dimitri dicte ou recopie
  // d'un formulaire — jamais quelque chose que l'opérateur invente.
  reservation_update_internal: {
    ecran: "calendrier",
    endpoint: "/api/beds24-mapping",
    action: "reservation_update_internal",
    // Le handler refuse tout verbe ≠ POST (405).
    methode: "POST",
    args_autorises: ["reservation_id", "fields"],
    // Même raison que pour le verrou du livret : l'identifiant du séjour ne se
    // prononce pas et ne se compose pas. Ici, ce qu'un séjour inventé produit
    // n'est pas un code ouvert mais une adresse EFFACÉE sur la mauvaise
    // réservation — sans aucun aperçu de ce qu'il y avait avant.
    args_resolus: {
      reservation_id: {
        objet: "reservation",
        par: ["logement", "quand", "sens", "rang"],
        requis: ["logement", "quand"],
      },
    },
    // ⚠️ Clés RECOPIÉES de `RESERVATION_SCOPE_B_WHITELIST` — la liste fermée du
    // serveur, ni plus courte ni plus longue. Une clé hors liste refuse
    // l'intention ENTIÈRE, côté socle comme côté serveur.
    args_formes: {
      fields: {
        cles: [
          "guest_address",
          "guest_city",
          "guest_postal_code",
          "children_count",
        ],
        texte: true,
        // ⚠️ DEUX CHAMPS EXCLUS EXPRÈS, ET LE SILENCE ÉTAIT PIRE QUE LE REFUS.
        // `guest_email` et `guest_phone` sont hors de la whitelist serveur
        // parce que le cron de synchro Beds24 les RÉÉCRIRAIT
        // (`lib/reservation-handlers.js:234-236`). Sans ces phrases,
        // « change le téléphone de la résa de demain » faisait disparaître
        // l'intention ENTIÈRE sans un mot (`lireGeste` rend `null`) : Dimitri
        // lisait une panne, ou un « je ne sais pas », sur un geste dont le
        // refus a une raison parfaitement dicible.
        cles_exclues: {
          // ⚠️ LE NOM ET LES NOTES ÉTAIENT AUSSI SILENCIEUX. Fermer la moitié
          // du trou laisse exactement le même symptôme (« il ne répond rien »)
          // sur les autres champs que Dimitri demandera aussi.
          guest_first_name:
            "Ce geste ne touche pas au nom du voyageur : la synchro Beds24 le réécrirait. Il ne corrige que l'adresse, la ville, le code postal et le nombre d'enfants.",
          guest_last_name:
            "Ce geste ne touche pas au nom du voyageur : la synchro Beds24 le réécrirait. Il ne corrige que l'adresse, la ville, le code postal et le nombre d'enfants.",
          notes:
            "Ce geste ne touche pas aux notes de la réservation : elles sont hors de la liste que le serveur accepte. Il ne corrige que l'adresse, la ville, le code postal et le nombre d'enfants.",
          guest_phone:
            "Ce geste ne touche pas au téléphone du voyageur : la synchro Beds24 le réécrirait à la prochaine passe. Il ne corrige que l'adresse, la ville, le code postal et le nombre d'enfants.",
          guest_email:
            "Ce geste ne touche pas à l'e-mail du voyageur : la synchro Beds24 le réécrirait à la prochaine passe. Il ne corrige que l'adresse, la ville, le code postal et le nombre d'enfants.",
        },
      },
    },
    titre: "Corriger l'adresse ou le nombre d'enfants",
    constatable:
      "les champs corrigés s'affichent dans l'onglet Voyageur de la réservation (modale ouverte depuis le calendrier) — rien n'est envoyé à personne, et la synchro Beds24 ne les réécrira pas",
    sujet: "voyageur",
    cout: ["rien"],
    ou_ca_part: ["personne"],
    sans_retour:
      "les valeurs remplacées ne sont gardées nulle part, et une chaîne vide EFFACE le champ : ce qui est écrasé ne revient pas",
    ne_fait_pas:
      "il n'envoie rien au voyageur, ne touche à aucun montant et ne remonte rien chez Beds24 : ce sont quatre champs internes, que la synchro ne réécrira pas",
    apercu_absent:
      "aucune lecture ne rend les quatre champs actuels de la réservation : il faudrait `reservation_champs_lire`. Une chaîne vide efface — écraser à l'aveugle est possible aujourd'hui.",
    dit_comme: [
      "Corrige l'adresse de ce voyageur",
      "Ils sont trois enfants, pas deux",
      "Mets à jour le code postal sur cette réservation",
    ],
    vague: 9,
    motif_requis: false,
    // ⚠️ CE LEVIER NE PORTE AUCUN MARQUAGE, ET CE QUI LE REFUSE À LA VOIX EST
    // `sans_retour` SEUL (3ᵉ garde de `refuseALaVoix`, src/lib/carte-dite.js).
    // Le jour où quelqu'un jugera cette phrase décorative et la « nettoiera »,
    // il rouvrira la voix sur un geste qui EFFACE un champ — en silence. Un
    // test d'identité le tient (`reperes-geste-resa.test.js`).
    autonome: false,
  },

  // ── `journey_composition_set` — LE LEVIER QUE MON SOCLE AVAIT COÛTÉ ───────
  // Écarté au premier passage (2026-09-09) faute d'arguments composés : sa
  // charge utile est un OBJET, et `lireGeste` ne connaissait que des valeurs
  // simples. Dimitri, le même jour : « élargis ton socle aux arguments
  // composés — tu as raison, c'était un manque, pas une prudence. »
  //
  // ⚠️ ET L'OBJET EST BORNÉ AUSSI FINEMENT QU'UN ARGUMENT SIMPLE : ses six
  // clés et ses trois valeurs sont recopiées de `BLOCS` et `MODES`
  // (`lib/journey-composition-resolver.js`), qui sont déjà la liste fermée du
  // serveur. Une clé ou une valeur hors liste refuse l'intention ENTIÈRE.
  //
  // ⚠️ MARQUÉ `sortant` : couper le reçu ou le formulaire, c'est décider ce
  // qu'un voyageur RECEVRA ou ne recevra plus. L'effet ne se voit pas chez
  // nous, il se voit chez lui — et il ne se voit qu'au prochain séjour.
  journey_composition_set: {
    ecran: "voyageurs-textes",
    endpoint: "/api/beds24-mapping",
    action: "journey_composition_set",
    args_autorises: ["scope", "plateforme", "blocs"],
    // ⚠️ Valeurs RECOPIÉES de la liste fermée du serveur. Sans elles, le
    // modèle compose du plausible — il l'a fait le 2026-09-09 en prod
    // (« adresse » pour « adresse_complete ») — et la carte s'affiche
    // avant d'échouer sous le clic.
    args_valeurs: {
      plateforme: ["booking", "airbnb", "direct"],
    },
    args_formes: {
      blocs: {
        cles: ["formulaire", "caution", "livret", "recu", "etat_lieux", "avis"],
        valeurs: ["auto", "on", "off"],
      },
    },
    titre: "Régler ce qui part au voyageur, par plateforme",
    constatable:
      "la matrice de la plateforme affiche les nouveaux réglages dans Messages automatiques — et c'est ce qui PARTIRA (ou ne partira plus) au voyageur, à partir du prochain séjour",
    sujet: "voyageur",
    cout: ["rien"],
    ou_ca_part: ["atteignable"],
    sans_retour: null, // la matrice se repose bloc par bloc, à l'identique
    // ⚠️ L'EFFET NE SE VOIT PAS CHEZ NOUS, ET PAS TOUT DE SUITE.
    ne_fait_pas:
      "il n'envoie ni n'annule aucun message en cours : il décide de ce qui partira AU PROCHAIN SÉJOUR de cette plateforme. Les séjours déjà entamés gardent leur composition.",
    apercu_absent:
      "aucune lecture ne rend la matrice actuelle de la plateforme : il faudrait `journey_composition_lire`. Couper un bloc sans voir les cinq autres, c'est régler ce qu'un voyageur recevra sans voir ce qu'il reçoit aujourd'hui.",
    dit_comme: [
      "Ne renvoie plus de reçu aux voyageurs Booking",
      "Coupe le formulaire d'arrivée sur Airbnb",
      "Règle ce qui part au voyageur selon la plateforme",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["sortant"],
  },

  // ══ VALIDER OU REFUSER UNE DEMANDE DIRECTE (2026-09-14) ══════════════════
  //
  // Dimitri : « valider ou refuser une demande directe ». CHEMIN ARGENT ET
  // SORTANT — et ce qui suit vient de la LECTURE de `handleSiteBookingValidate`
  // en entier, pas de son en-tête.
  //
  // ⚠️ CE QUE LA DEMANDE ANNONÇAIT N'EST PLUS VRAI DEPUIS L'« ÉTAPE 3 », et le
  // `constatable` doit dire ce qui se passe AUJOURD'HUI, pas ce qui se passait :
  // la RPC est ALLÉGÉE. Elle NE crée PLUS le `pricing_block` (les dates NE se
  // bloquent PAS) et NE crée PLUS les deux `form_tokens` — les commentaires du
  // handler l'écrivent noir sur blanc : « la création du bloc + hold +
  // mark-promo + form_tokens est déplacée au checkout (action
  // checkout_start) ». Annoncer « les dates sont bloquées » serait faux, et
  // c'est précisément le genre de fausseté qui coûte un double-booking.
  //
  // CE QUI SE PASSE VRAIMENT :
  //   · VALIDER  → la demande passe `pending` → `awaiting_signature`, un
  //     `journey_token` est posé (idempotent : un lien /sejour déjà envoyé
  //     n'est jamais tué), et l'e-mail E3 PART CHEZ LE VOYAGEUR avec son lien.
  //   · REFUSER  → la demande passe `rejected`, le hold du code promo est
  //     libéré (2 tentatives, alerte admin si échec), et l'e-mail E4 PART CHEZ
  //     LE VOYAGEUR.
  //   · Dans les deux cas le serveur REFUSE (409) si la demande n'est plus
  //     `pending` — anti-course entre deux admins — et 423 sur un verrou.
  //
  // ⚠️ ET L'E-MAIL PART VRAIMENT, MÊME EN `MESSAGES_DRY_RUN` (ADR-272) : les
  // envois TRANSACTIONNELS du parcours Direct passent par `lib/sender-email.js`
  // / `lib/email-i18n-sender.js`, qui n'ont AUCUN gate. Le `constatable` le dit
  // — croire qu'un drapeau protège ici serait l'erreur exacte que l'ADR-272
  // documente.
  //
  // ⚠️ L'ACTION APPELÉE EST `site_booking_decision`, PAS `site_booking_validate`,
  // ET CE N'EST PAS UN CONTOURNEMENT. `site_booking_validate` attend sa
  // décision dans un paramètre nommé `action` — le nom même que le moteur de
  // gestes met dans le corps pour router (`{ action: levier.action, ...args }`).
  // Un levier direct aurait donc envoyé `action: "validated"` et reçu « action
  // inconnue » : un bouton mort, découvert sous le clic. La porte
  // `lib/booking-decision-handlers.js` renomme l'argument en `decision` et
  // DÉLÈGUE, sans recopier une ligne du chemin argent.
  //
  // ⚠️ `notes` N'EST PAS DANS LA LISTE, et c'est la règle non négociable : le
  // motif de rejet part DANS L'E-MAIL au voyageur quand il est rendu public.
  // Un texte destiné à un tiers n'entre jamais dans une intention composée.
  // Sans lui, le serveur envoie sa phrase de motif par défaut ; Dimitri peut
  // toujours rejeter AVEC un motif écrit depuis son écran.
  //
  // ⚠️ VAGUE 4, ET C'EST ARGUMENTÉ PLUTÔT QU'OUVERT LARGE. Les cinq vagues
  // disponibles sont 1 (la mécanique fait ses preuves), 2 (rangement), 3
  // (contenu éditorial), 4 (réglages bornés) et 9 (les textes). Ce geste n'est
  // aucune des trois premières : il sort de chez nous et engage un séjour. La 9
  // ne lui va pas non plus — il ne porte aucun texte. Reste la 4, qui porte
  // déjà les gestes d'argent (`logement_commission_set`, `multiplier_set`) et
  // les sortants (`pricing_block_create`, `journey_composition_set`) : c'est
  // son groupe, et c'est la dernière vague que Dimitri ouvrira.
  site_booking_decision: {
    ecran: "voyageurs-demandes",
    endpoint: "/api/beds24-mapping",
    action: "site_booking_decision",
    methode: "POST",
    args_autorises: ["request_id", "decision"],
    // ⚠️ L'IDENTIFIANT D'UNE DEMANDE NE SE PRONONCE PAS, ET CELUI-LÀ ENVOIE
    // DEUX E-MAILS IRRÉVOCABLES. Le modèle ne l'a vu nulle part : s'il en
    // composait un, il l'aurait inventé — et le voyageur d'un autre séjour
    // recevrait un lien de réservation ou un refus. Le repère se résout au
    // serveur (`lib/resa-repere.js`), sur les demandes `pending` NON EXPIRÉES
    // du logement dit : le seul état que le serveur accepte (409 sinon).
    // L'aperçu et son jeton restent intouchés — c'est lui qui montre le
    // montant et le prénom, sur une ligne DÉJÀ choisie.
    args_resolus: {
      request_id: {
        objet: "demande_directe",
        // Une demande n'a pas de sens d'arrivée : le rang suffit à répondre.
        par: ["logement", "quand", "rang"],
        requis: ["logement", "quand"],
      },
    },
    // Valeurs RECOPIÉES de la liste fermée du serveur — un test vérifie
    // qu'elles lui sont égales, dans les deux sens.
    args_valeurs: { decision: ["validated", "rejected"] },
    apercu: {
      lecture: "booking_decision_lire",
      depend_de: ["request_id"],
      // ⚠️ `x-apercu-version`, PAS `x-cfg-version` — ce geste n'écrit pas le
      // blob de configuration, et un en-tête qui ment sur ce qu'il transporte
      // finit lu de travers. Le jeton est dérivé du STATUT, du MONTANT et des
      // DATES : les faits sur lesquels Dimitri décide, et que la carte lui
      // montre. Si l'un a bougé depuis l'affichage, la porte refuse (409) et
      // renvoie le jeton frais — l'effet du clic étant un e-mail irrévocable
      // chez un voyageur, un jeton décoratif serait pire que pas de jeton.
      jeton: "x-apercu-version",
    },
    titre: "Valider ou refuser une demande de réservation directe",
    constatable:
      "la demande change d'état dans Voyageurs › Demandes — acceptée, elle passe en « attente de signature » et un e-mail PART CHEZ LE VOYAGEUR avec son lien de séjour ; refusée, elle passe en « refusée », la remise qu'elle réservait est rendue, et un e-mail de refus PART CHEZ LE VOYAGEUR. Ces deux e-mails partent VRAIMENT, même quand l'app est en mode simulation. La carte montre le montant en € de la demande avant le clic. ⚠️ Les dates ne sont PAS bloquées à cette étape : le blocage et le paiement viennent plus tard, quand le voyageur clique « payer ». Le geste est refusé si quelqu'un a déjà décidé entre-temps.",
    sujet: "voyageur",
    cout: ["argent", "envoi"],
    ou_ca_part: ["voyageur"],
    // ⚠️ LA CARTE DE RÉFÉRENCE : elle disait déjà ce qui ne se passe PAS, dans
    // son `constatable`. La phrase passe ici, où le champ existe maintenant —
    // le `constatable` la garde aussi, parce que c'est lui qui s'affiche.
    ne_fait_pas:
      "les dates ne sont PAS bloquées à cette étape : le blocage et les jetons de paiement sont posés plus tard, quand le voyageur clique « payer ». Valider n'encaisse rien non plus.",
    dit_comme: [
      "Valide cette demande de réservation",
      "Refuse cette demande directe",
      "Accepte la demande de la semaine prochaine",
    ],
    vague: 4,
    motif_requis: false,
    autonome: false,
    marquages: ["argent", "sortant"],
    sans_retour:
      "Les deux e-mails partent chez le voyageur dès le clic : ni l'un ni l'autre ne se rappelle, et une demande décidée ne revient pas en attente.",
    // E1 ✔ (aucun mouvement de fonds à cet instant — le paiement se fait plus
    // tard, au checkout ; l'e-mail parti n'est pas « détruit », il est envoyé,
    // et c'est le marquage `sortant` qui le dit) · E2 ✔ (la file des demandes
    // change à l'écran) · E3 ✔ (délègue à l'écrivain COMPLET — c'est le remède
    // d'E3, pas son infraction) · E4 ✔ (rien n'est attesté : décider n'est pas
    // affirmer un fait non observé) · E5 ✔.
  },
});

// ══ DEUX ABSENCES EXPLIQUÉES (2026-09-13) — pour ne pas les redécouvrir ═════
//
// ── `form_link_send{reservation_id}` — N'EXISTE PAS, ET ON NE L'A PAS FABRIQUÉ.
// L'événement « arrive demain sans formulaire » (`lib/operateur-evenements.js`,
// `arrive_sans_formulaire`) appelle un envoyeur. Vérifié sur le routeur et les
// modules : il n'y a que des GÉNÉRATEURS de lien —
//   · `guest_link_shorten` (kind `form`) raccourcit une URL, n'envoie rien ;
//   · `lib/form-tokens.js` / `form-token-helpers.js` créent le jeton, rien de
//     plus ;
//   · l'envoi du formulaire est AUTOMATIQUE (`message_sends` / moteur des
//     messages Plateformes, parcours Direct) — aucune action « envoie-le
//     maintenant » n'est routée.
// Un levier qui prétendrait envoyer et ne ferait que générer serait un bouton
// menteur. Ce qu'il faudrait : une action de domaine (lib/, pas le routeur)
// qui résout le jeton `unified`/`welcome` de la résa (le crée s'il manque),
// bâtit l'URL, et la fait partir par le guichet `api/send-email.js` (canal
// tchat OTA via `beds24BookingId`, gaté MESSAGES_DRY_RUN, trace
// `inbox_messages`) — jamais par un sender neuf. Et un test qui rougit le jour
// où `form_link_send` apparaît au routeur sans levier ni justification
// (`src/lib/leviers-envois.test.js`).
// En attendant, le chemin honnête existe : Dimitri demande une réponse
// (`inbox_reply`, famille messages) dont le corps porte le lien — c'est lui
// qui l'écrit, et il voit ce qui part.
//
// ── RÉPONDRE À UN AVIS — AUCUN LEVIER, PAR CONSTRUCTION.
// Dimitri : « qu'il prépare, je colle. Pas de bouton mort. »
// Vérifié : `lib/avis-sync.js` LIT les avis (sync lecture seule, doctrine
// DGCCRF ADR-183/198) ; `api/_lib/beds24/*` n'expose aucune réponse à un avis ;
// aucune action `avis_*`/`review_*` d'écriture n'est routée. Ni Airbnb ni
// Booking ne s'atteignent depuis l'app pour ça. L'opérateur PRÉPARE le texte
// dans sa réponse (registre `repondre`), Dimitri le colle sur la plateforme.
// Un levier ici n'aurait rien à appeler — le cartouche du registre l'interdit
// (« ne jamais afficher un bouton qui n'agit pas »).

// ── RELANCER UN CONTRAT EN ATTENTE — REFUSÉ, PARCE QUE RIEN N'EXISTE (2026-09-14)
//
// Demandé par Dimitri le même jour que les cinq autres. Cherché AVANT
// d'écrire quoi que ce soit, dans `lib/direct-journey-sweep.js`,
// `lib/journey-templates.js`, `lib/sender-email.js`, `api/sites.js` et les
// deux routeurs. VERDICT : AUCUNE action de relance de signature n'existe, et
// on ne fabrique pas un levier qui pointerait dans le vide — c'est exactement
// le « fantôme » que `src/lib/operateur-leviers-ecartes.test.js` interdit.
//
// CE QUI EXISTE, ET POURQUOI AUCUN NE CONVIENT :
//   · `contract_read` / `contract_sign` (api/sites.js) sont ANONYMES et gatés
//     par le `journey_token` du voyageur : ce sont SES gestes à lui, pas ceux
//     de Dimitri. Composer une signature à sa place serait E4 dans sa forme la
//     plus nette.
//   · `contract_download_url` et `contract_pdf_get` servent le PDF d'un
//     contrat DÉJÀ SIGNÉ. Rien à relancer là.
//   · le SWEEP d'expiration (`lib/direct-journey-sweep.js`) fait l'inverse
//     d'une relance : à 72 h, il fait passer la demande `awaiting_signature` →
//     `expired`, et son e-mail va à l'ADMIN, pas au voyageur.
//   · les TROIS rappels `caution_reminder_j0/j1/j2` existent bel et bien, mais
//     ils portent sur la CAUTION et le FORMULAIRE, jamais sur la signature — et
//     ils ne se déclenchent que dans la fenêtre J-2 → J0 avant l'arrivée, sur
//     des résas déjà `paid_stay`/`paid_caution`. Un contrat en attente n'y
//     entre jamais.
//   · `inbox_reply` (famille messages), le seul levier dont le clic fait partir
//     un message, a son canal FERMÉ à `tchat_ota` : il n'atteint pas un
//     voyageur DIRECT, qui n'a pas de fil de plateforme.
//
// CE QU'IL FAUDRAIT ÉCRIRE D'ABORD (et c'est un chantier, pas une ligne) :
// une action de domaine qui, sur une demande `awaiting_signature`, RÉUTILISE
// son `journey_token` existant (jamais en régénérer un : cela tuerait le lien
// déjà envoyé — la garde d'idempotence de `site_booking_validate` existe pour
// ça) et renvoie le gabarit de rappel de signature… qui reste à écrire lui
// aussi : il n'y a aucun `signature_reminder_*` dans `journey-templates.js`.
//
// ⚠️ ET LA DÉCISION D'ENVOI SERAIT CELLE DE DIMITRI, PAS UN DÉTAIL TECHNIQUE
// (ADR-272). Un rappel est TEMPOREL : les envois temporels du parcours Direct
// sont gatés par `MESSAGES_DRY_RUN` (ils posent le claim et sautent l'envoi).
// Mais un rappel déclenché À LA MAIN par Dimitri n'est plus temporel — il est
// transactionnel, et partirait RÉELLEMENT. Le `constatable` devrait le dire, et
// le choix du gate est une décision produit, pas une ligne de code.
//
// En attendant, le chemin honnête existe et ne ment pas : Dimitri voit la
// demande dans Voyageurs › Demandes, et relance lui-même.

// ══ LES BOOLÉENS DE CETTE FAMILLE, EN MOTS — `levier.argument` ════════════
//
// F5 (03/10/2026) : déplacées telles quelles de `lib/operateur-leviers.js`,
// qui les rassemble (la règle et ses gardes sont écrites là-bas, au-dessus de
// `NOMS_BOOLEENS`). Une phrase se déclare ICI, à côté de son levier, et nulle
// part ailleurs.
const BOOLEENS_LIBELLES = Object.freeze({
  "sejours_marquer_lot.confirmer": {
    oui: "Écrire le marquage",
    non: "Montrer le lot sans rien écrire",
  },
});

module.exports = { LEVIERS_VOYAGEURS, BOOLEENS_LIBELLES };
